import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Alert, Portal, Snackbar } from '@mui/material';

type Severity = 'error' | 'warning' | 'info' | 'success';

interface ToastState {
  key: number;
  message: string;
  severity: Severity;
}

interface ToastApi {
  showToast: (message: string, severity?: Severity) => void;
  toastError: (message: string) => void;
  toastSuccess: (message: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

/**
 * App-wide snackbar. MUI's Snackbar sits above Dialogs (z-index 1400 vs 1300),
 * so failures from a save inside a modal are visible — page-level Alerts are
 * hidden behind the backdrop. Rendered through a Portal so an open Dialog's
 * aria-hidden on #root does not hide the toast from assistive tech.
 */
export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);

  const showToast = useCallback((message: string, severity: Severity = 'info') => {
    setToast({ key: Date.now(), message, severity });
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      showToast,
      toastError: (message) => showToast(message, 'error'),
      toastSuccess: (message) => showToast(message, 'success'),
    }),
    [showToast]
  );

  const close = () => setToast(null);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <Portal>
        <Snackbar
          key={toast?.key}
          open={toast !== null}
          autoHideDuration={toast?.severity === 'error' ? 8000 : 4000}
          onClose={(_, reason) => {
            if (reason !== 'clickaway') {
              close();
            }
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={toast?.severity ?? 'info'}
            variant="filled"
            onClose={close}
            sx={{ width: '100%', maxWidth: 560 }}
          >
            {toast?.message}
          </Alert>
        </Snackbar>
      </Portal>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return ctx;
}
